import { useCallback, useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { useAuth } from '@/auth/context'
import { SessionExpired } from '@/auth/SessionExpired'
import { panelForPath } from '@/pages/settings/panels'
import { useActiveTenant } from '@/tenant/context'
import { SetupBanner } from './SetupBanner'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import type { Crumb } from './TopBar'
import { MODULE_STORAGE_KEY, moduleById, moduleForPath, rememberedModule } from './navigation'
import type { ModuleId } from './navigation'

/**
 * The frame every signed-in screen sits inside.
 *
 * Converted once, from S-D3, so no later session redraws navigation — which is
 * the whole reason this session exists ahead of the module sessions.
 *
 * The module is derived from the URL where the URL says (`/seo`, `/outreach`)
 * and from the remembered choice where it does not (`/settings`, which belongs
 * to both). Storing it is what stops a settings visit from silently moving
 * somebody out of the module they were working in.
 */
export function AppShell(): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  const { tenant } = useActiveTenant()
  const { expired } = useAuth()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const fromPath = moduleForPath(location.pathname)
  const [remembered, setRemembered] = useState<ModuleId>(() =>
    rememberedModule(window.localStorage),
  )
  const module = fromPath ?? remembered

  useEffect(() => {
    if (fromPath) {
      setRemembered(fromPath)
      window.localStorage.setItem(MODULE_STORAGE_KEY, fromPath)
    }
  }, [fromPath])

  // The drawer is a route-level overlay; leaving it open across a navigation
  // would cover the screen the user just asked for.
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  const onModuleChange = useCallback(
    (id: ModuleId) => {
      setRemembered(id)
      window.localStorage.setItem(MODULE_STORAGE_KEY, id)
      void navigate(moduleById(id).home)
    },
    [navigate],
  )

  const crumbs = useMemo<Crumb[]>(() => {
    const definition = moduleById(module)
    const trail: Crumb[] = [
      { label: tenant.name, to: '/organisations' },
      { label: definition.label, to: definition.home },
    ]
    if (location.pathname.startsWith('/settings')) {
      trail.push({ label: 'Settings', to: '/settings/organisation' })
      const panel = panelForPath(location.pathname)
      if (panel) trail.push({ label: panel.label })
    } else {
      trail.push({ label: 'Overview' })
    }
    return trail
  }, [module, tenant.name, location.pathname])

  return (
    <div className="app-shell">
      <Sidebar module={module} onModuleChange={onModuleChange} />

      <div className="app-main">
        <TopBar
          crumbs={crumbs}
          onOpenMenu={() => {
            setDrawerOpen(true)
          }}
        />

        <main className="app-body">
          <SetupBanner />
          <Outlet />
        </main>

        <MobileBar module={module} />
      </div>

      {drawerOpen ? (
        <div
          className="ar-drawer"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDrawerOpen(false)
          }}
        >
          <div className="ar-drawer__panel" style={{ inset: '0 auto 0 0' }}>
            <Sidebar
              module={module}
              variant="drawer"
              onModuleChange={onModuleChange}
              onNavigate={() => {
                setDrawerOpen(false)
              }}
            />
            <div className="ar-drawer__footer">
              <button
                type="button"
                className="ar-btn ar-btn--ghost ar-btn--block"
                onClick={() => {
                  setDrawerOpen(false)
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {expired ? <SessionExpired /> : null}
    </div>
  )
}

/**
 * The bottom bar below 1100px.
 *
 * The first four sections of the current module, so what a tenant reaches for
 * on a phone is one tap away. Sections whose module has not shipped keep their
 * place and say so, for the same reason the sidebar does.
 */
function MobileBar({ module }: { module: ModuleId }): JSX.Element {
  const definition = moduleById(module)
  return (
    <nav className="app-mobile-bar" aria-label={`${definition.label} (compact)`}>
      {definition.sections.slice(0, 4).map((section) =>
        section.to ? (
          <NavLink key={section.label} to={section.to} end={section.to === definition.home}>
            {section.label}
          </NavLink>
        ) : (
          <span key={section.label} aria-disabled="true">
            {section.label}
          </span>
        ),
      )}
    </nav>
  )
}
